import { AnimatePresence } from 'motion/react';
import { useMemo } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import { ALL_ROUTES } from '../consts/routes';
import { useAuthenticatedRoutes } from './useAuthenticatedRoutes';
import { useAuthRoutes } from './useAuthRoutes';
import { useFallbackRoutes } from './useFallbackRoutes';
import { useRouteEffects } from './useRouteEffects';
import { useSharedRoutes } from './useSharedRoutes';
import { useAuth } from '@/Bridges/GLUtils';
import { usePrivileges } from '@/hooks/usePrivilleges';

/**
 * Main routes orchestrator
 * Combines all route hooks and manages route effects
 */
export const useCreateAllRoutes = () => {
  const location = useLocation();
  const { accessToken, storeIsReady } = useAuth();
  const { pages } = usePrivileges();

  // Executar side effects
  useRouteEffects();

  // Determinar rota padrão (baseado em feature flags)
  const indexRoute = useMemo(() => {
    return pages.api ? ALL_ROUTES.API : ALL_ROUTES.REPORTS;
  }, [pages]);

  const isAuthenticated = useMemo(() => {
    return Boolean(accessToken && storeIsReady);
  }, [accessToken, storeIsReady]);

  // Obter todas as definições de rotas
  const authRoutes = useAuthRoutes();
  const sharedRoutes = useSharedRoutes();
  const authenticatedRoutes = useAuthenticatedRoutes(indexRoute);
  const fallbackRoutes = useFallbackRoutes();

  // Combinar rotas baseado em autenticação
  const routeObjects: RouteObject[] = useMemo(
    () => [
      ...(isAuthenticated ? [] : authRoutes),
      ...sharedRoutes,
      ...(isAuthenticated ? authenticatedRoutes : []),
      ...fallbackRoutes,
    ],
    [authRoutes, sharedRoutes, authenticatedRoutes, fallbackRoutes, isAuthenticated],
  );

  const routes = useRoutes(routeObjects);

  // Wrap com AnimatePresence para transições
  return useMemo(
    () => (
      <AnimatePresence mode='wait' initial={false}>
        <div key={location.pathname}>{routes}</div>
      </AnimatePresence>
    ),
    [location.pathname, routes],
  );
};
